import { useQuery } from '@tanstack/react-query'
import { getOrders, getOrder } from '../services/auth-api'
import type { OrderFilters } from '../services/auth-api'

/**
 * Fetch orders list with optional search, pagination, and approval-queue
 * filters from backend API.
 */
export function useOrders(
  search?: string,
  page?: number,
  myOnly?: boolean,
  pageSize?: number,
  filters?: OrderFilters,
) {
  return useQuery({
    queryKey: ['orders', search, page, myOnly, pageSize, filters],
    queryFn: () => getOrders(search, page, myOnly, pageSize, filters),
    // Orders is a live work-queue, so override the global stable-data defaults
    // (refetchOnWindowFocus:false, staleTime:5m). Refetch when the admin returns
    // to the tab — no background polling. staleTime:30s so quick tab-flicks don't
    // refetch, but coming back after a real gap (new orders arrived) does.
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
    // Smooth pagination only: keep the current rows visible when the *sole*
    // change is the page number. Tab / filter / search changes return no
    // placeholder, so they show a fresh loading state instead of briefly
    // flashing the previous view's data (e.g. My Team rows on the Requires
    // Approval tab).
    placeholderData: (previousData, previousQuery) => {
      if (!previousQuery) return undefined
      const prevKey = previousQuery.queryKey as unknown[]
      const sameExceptPage =
        prevKey[1] === search &&
        prevKey[3] === myOnly &&
        prevKey[4] === pageSize &&
        JSON.stringify(prevKey[5]) === JSON.stringify(filters)
      return sameExceptPage ? previousData : undefined
    },
  })
}

/**
 * Fetch single order by ID from backend API
 *
 * Polling is handled by components via useEffect + setInterval
 * for conditional polling based on order status.
 */
export function useOrder(id: string) {
  return useQuery({
    queryKey: ['orders', id],
    queryFn: () => getOrder(id),
    enabled: !!id,
    // Refetch on window focus to catch changes from other tabs
    refetchOnWindowFocus: true,
  })
}
